import { useEffect, useState } from 'react';
import { Check, Pencil, Loader2, Lock } from 'lucide-react';
import { useAuth } from '@/store/auth-context';
import { useToast } from '@/components/ui/Toast';
import { authApi, ApiError } from '@/services/api';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ProfilePicturePicker } from '@/components/profile/ProfilePicturePicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Badge from '@/components/ui/Badge';
import { TimezoneCombobox } from '@/components/ui/TimezoneCombobox';
import { getBrowserTimezone } from '@/utils/timezone';

export default function ProfileSection() {
  const { user } = useAuth();

  if (!user) return null;

  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold text-foreground">Profile</h2>
      <ProfileHeaderCard />
      <PersonalInformationCard />
      <SecurityCard />
    </section>
  );
}

// ─── Header card ─────────────────────────────────────────────────────────

function ProfileHeaderCard() {
  const { user, updateProfilePicture } = useAuth();
  const toast = useToast();
  const [switching, setSwitching] = useState<string | null>(null);
  if (!user) return null;

  const initials =
    `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() ||
    user.email[0]?.toUpperCase();

  const displayName =
    [user.firstName, user.lastName].filter(Boolean).join(' ') ||
    user.email.split('@')[0];

  const roleBadgeVariant = user.role === 'admin' ? 'info' : 'gray';

  const pictures = user.profilePictures ?? [];
  const activeSource = user.activeProfilePictureSource;

  const selectPicture = async (source: string) => {
    if (source === activeSource || switching) return;
    setSwitching(source);
    try {
      await updateProfilePicture(source);
      toast.success('Profile picture updated');
    } catch {
      toast.error('Failed to update profile picture');
    } finally {
      setSwitching(null);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
      <div className="flex items-center gap-4">
        <Avatar className="h-16 w-16">
          <AvatarImage src={user.avatarUrl} alt={displayName} />
          <AvatarFallback className="bg-primary-600 text-white text-xl">{initials}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-base font-semibold text-foreground">{displayName}</span>
            <Badge variant={roleBadgeVariant} className="capitalize">
              {user.role}
            </Badge>
          </div>
          {(user.extendedProfile?.title || user.timezone) && (
            <div className="mt-1 text-sm text-muted-foreground">
              {user.extendedProfile?.title || user.timezone}
            </div>
          )}
        </div>
      </div>

      {pictures.length > 1 && (
        <div className="mt-5 border-t border-border pt-4">
          <ProfilePicturePicker
            pictures={pictures}
            activeSource={activeSource}
            initials={initials || '?'}
            switchingSource={switching}
            onSelect={selectPicture}
          />
        </div>
      )}
    </div>
  );
}

// ─── Personal information card ───────────────────────────────────────────

function PersonalInformationCard() {
  const { user, updateProfile } = useAuth();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [timezone, setTimezone] = useState('');

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
      setTimezone(user.timezone || getBrowserTimezone());
    }
  }, [user, editing]);

  if (!user) return null;

  const profileDepartment = user.extendedProfile?.department;

  const startEdit = () => setEditing(true);
  const cancelEdit = () => {
    setFirstName(user.firstName || '');
    setLastName(user.lastName || '');
    setTimezone(user.timezone || getBrowserTimezone());
    setEditing(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        timezone,
      });
      toast.success('Profile updated');
      setEditing(false);
    } catch {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
      <div className="mb-5 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Personal information</h3>
        {!editing && (
          <Button variant="outline" size="sm" onClick={startEdit}>
            <Pencil className="mr-2 h-3.5 w-3.5" />
            Edit
          </Button>
        )}
      </div>

      {editing ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="First name"
              placeholder="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={saving}
            />
            <Input
              label="Last name"
              placeholder="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={saving}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Timezone</label>
            <TimezoneCombobox value={timezone} onChange={setTimezone} disabled={saving} />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={cancelEdit} disabled={saving}>
              Cancel
            </Button>
            <Button size="sm" onClick={save} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </div>
        </div>
      ) : (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
          <Field label="First name" value={user.firstName} />
          <Field label="Last name" value={user.lastName} />
          <Field label="Role" value={user.role} className="capitalize" />
          {profileDepartment && <Field label="Department" value={profileDepartment} />}
          <Field label="Timezone" value={user.timezone} />
          <Field label="Email" value={user.email} />
        </dl>
      )}
    </div>
  );
}

function Field({ label, value, className }: { label: string; value?: string; className?: string }) {
  return (
    <div className={className}>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-sm text-foreground">{value || '—'}</dd>
    </div>
  );
}

// ─── Security card ───────────────────────────────────────────────────────

type AuthMethodState = 'unknown' | 'password' | 'okta';

function SecurityCard() {
  const { user, changePassword } = useAuth();
  const toast = useToast();
  const [method, setMethod] = useState<AuthMethodState>('unknown');
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentError, setCurrentError] = useState<string | undefined>();
  const [newError, setNewError] = useState<string | undefined>();
  const [confirmError, setConfirmError] = useState<string | undefined>();

  useEffect(() => {
    if (!user?.email) return;
    let cancelled = false;
    authApi
      .discover(user.email)
      .then((res) => {
        if (cancelled) return;
        setMethod(res.method === 'okta' ? 'okta' : 'password');
      })
      .catch(() => {
        // If discover fails, default to password — the backend will still gate via 403.
        if (!cancelled) setMethod('password');
      });
    return () => {
      cancelled = true;
    };
  }, [user?.email]);

  if (!user) return null;

  const reset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setCurrentError(undefined);
    setNewError(undefined);
    setConfirmError(undefined);
  };

  const close = () => {
    reset();
    setOpen(false);
  };

  const submit = async () => {
    setCurrentError(undefined);
    setNewError(undefined);
    setConfirmError(undefined);

    if (!currentPassword) {
      setCurrentError('Required');
      return;
    }
    if (!newPassword) {
      setNewError('Required');
      return;
    }
    if (newPassword === currentPassword) {
      setNewError('New password must differ from current password');
      return;
    }
    const policyError = validatePasswordPolicy(newPassword);
    if (policyError) {
      setNewError(policyError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setConfirmError("Passwords don't match");
      return;
    }

    setSubmitting(true);
    try {
      await changePassword({ currentPassword, newPassword });
      toast.success('Password updated');
      close();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : undefined;
      switch (code) {
        case 'INCORRECT_PASSWORD':
          setCurrentError('Current password is incorrect');
          break;
        case 'VALIDATION_ERROR':
          setNewError("Password doesn't meet requirements");
          break;
        case 'FEDERATED_USER':
          setMethod('okta');
          toast.error('Password is managed by your identity provider');
          break;
        case 'RATE_LIMITED':
          toast.error('Too many attempts — please try again later');
          break;
        default:
          toast.error("Couldn't update password");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Security</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {method === 'okta'
              ? 'Your password is managed by Okta.'
              : 'Update the password used to sign in.'}
          </p>
        </div>
        {method === 'password' && !open && (
          <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
            <Lock className="mr-2 h-3.5 w-3.5" />
            Change password
          </Button>
        )}
      </div>

      {method === 'okta' && (
        <div className="mt-4 rounded-md border border-dashed border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          Sign in with Okta to manage your password.
        </div>
      )}

      {method === 'password' && open && (
        <div className="mt-5 space-y-4">
          <Input
            type="password"
            label="Current password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            error={currentError}
            disabled={submitting}
            autoComplete="current-password"
          />
          <Input
            type="password"
            label="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={newError}
            disabled={submitting}
            autoComplete="new-password"
          />
          <PolicyHints password={newPassword} />
          <Input
            type="password"
            label="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={confirmError}
            disabled={submitting}
            autoComplete="new-password"
          />
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={close} disabled={submitting}>
              Cancel
            </Button>
            <Button size="sm" onClick={submit} disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update password
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Password policy ─────────────────────────────────────────────────────

const PASSWORD_RULES: { label: string; test: (v: string) => boolean }[] = [
  { label: 'At least 12 characters', test: (v) => v.length >= 12 },
  { label: 'Lowercase letter', test: (v) => /[a-z]/.test(v) },
  { label: 'Uppercase letter', test: (v) => /[A-Z]/.test(v) },
  { label: 'Number', test: (v) => /\d/.test(v) },
  { label: 'Symbol', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

function validatePasswordPolicy(value: string): string | undefined {
  for (const rule of PASSWORD_RULES) {
    if (!rule.test(value)) return `Password must include: ${rule.label.toLowerCase()}`;
  }
  return undefined;
}

function PolicyHints({ password }: { password: string }) {
  return (
    <ul className="space-y-1 text-xs text-muted-foreground">
      {PASSWORD_RULES.map((rule) => {
        const ok = password.length > 0 && rule.test(password);
        return (
          <li key={rule.label} className="flex items-center gap-2">
            <Check
              className={`h-3 w-3 ${ok ? 'text-status-success-fg' : 'text-muted-foreground/40'}`}
            />
            <span className={ok ? 'text-foreground' : ''}>{rule.label}</span>
          </li>
        );
      })}
    </ul>
  );
}
